import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getBookingReceipt, type BookingReceipt } from "@/lib/receipt.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/receipt/$bookingId")({
  head: () => ({ meta: [{ title: "Booking receipt — The Hospitality Reserve" }] }),
  component: ReceiptPage,
});

function gbp(pence: number) {
  return `£${(pence / 100).toFixed(2)}`;
}
function fmt(s: string | null) {
  return s ? new Date(s).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
}

function ReceiptPage() {
  const { bookingId } = Route.useParams();
  const fetchReceipt = useServerFn(getBookingReceipt);
  const [data, setData] = useState<BookingReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchReceipt({ data: { booking_id: bookingId } }).then((r) => {
      if (!active) return;
      if ("error" in r) setError(r.error);
      else setData(r);
    }).catch((e) => active && setError(e instanceof Error ? e.message : "Failed to load receipt."));
    return () => { active = false; };
  }, [bookingId, fetchReceipt]);

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-display text-2xl">Receipt unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link to="/dashboard"><Button variant="outline" className="rounded-none">Back to dashboard</Button></Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-background text-foreground flex items-center justify-center"><p className="text-sm text-muted-foreground">Loading receipt…</p></div>;
  }

  const statusLabel = data.cancelled_at
    ? "Cancelled"
    : data.completed_at
      ? "Completed"
      : data.arrival_disputed_at
        ? "Disputed — under review"
        : "In progress";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } }`}</style>
      <div className="mx-auto max-w-3xl p-6 print:p-0 print:max-w-none">
        <div className="no-print flex items-center justify-between mb-4">
          <Link to="/dashboard" className="text-xs tracking-wide-luxury underline">← Back</Link>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none tracking-wide-luxury" onClick={() => window.print()}>
              Print / Save as PDF
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card p-8 print:border-0 print:p-6">
          <header className="flex items-start justify-between border-b border-border pb-4 mb-6">
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground">The Hospitality Reserve</p>
              <h1 className="font-display text-2xl mt-1">Booking receipt</h1>
              <p className="text-xs text-muted-foreground mt-1">Booking #{data.booking_id.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs tracking-wide-luxury text-muted-foreground">Status</p>
              <p className="font-display text-lg mt-1">{statusLabel}</p>
              <p className="text-xs text-muted-foreground">Issued {fmt(data.generated_at)}</p>
            </div>
          </header>

          <section className="grid grid-cols-2 gap-6 mb-6 text-sm">
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Venue</p>
              <p>{data.venue_name}</p>
              {(data.address || data.postcode) && (
                <p className="text-xs text-muted-foreground mt-1">{[data.address, data.postcode].filter(Boolean).join(", ")}</p>
              )}
            </div>
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Bartender</p>
              <p>{data.bartender_name}</p>
              <p className="text-xs text-muted-foreground mt-1">{data.role_type}</p>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-6 mb-6 text-sm">
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Scheduled start</p>
              <p>{fmt(data.start_time)}</p>
            </div>
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Scheduled end</p>
              <p>{fmt(data.end_time)}</p>
            </div>
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Logged arrival</p>
              <p>{fmt(data.actual_arrival_at)}</p>
            </div>
            <div>
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Completed</p>
              <p>{fmt(data.completed_at)}</p>
            </div>
          </section>

          <section className="border-t border-border pt-4">
            <p className="text-xs tracking-wide-luxury text-muted-foreground mb-3">Payout breakdown</p>
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1">Hourly rate</td><td className="text-right">{gbp(Math.round(data.hourly_rate_gbp * 100))} / hr</td></tr>
                <tr><td className="py-1">Hours scheduled</td><td className="text-right">{data.hours.toFixed(2)} h</td></tr>
                <tr><td className="py-1">Billable hours (after unpaid break)</td><td className="text-right">{data.billable_hours.toFixed(2)} h</td></tr>
                <tr className="border-t border-border"><td className="py-2">Gross pay</td><td className="text-right">{gbp(data.gross_pence)}</td></tr>
                {data.late_minutes > 0 || data.late_deduction_pence > 0 ? (
                  <>
                    <tr><td className="py-1">Minutes late</td><td className="text-right">{data.late_minutes} min</td></tr>
                    <tr><td className="py-1 text-destructive">Late-arrival deduction</td><td className="text-right text-destructive">− {gbp(data.late_deduction_pence)}</td></tr>
                  </>
                ) : null}
                <tr className="border-t border-border font-display text-base">
                  <td className="py-3">Net payout</td>
                  <td className="text-right py-3">{gbp(data.net_payout_pence)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {data.arrival_disputed_at && (
            <section className="mt-6 border border-border p-4 text-sm">
              <p className="text-xs tracking-wide-luxury text-muted-foreground mb-1">Arrival dispute</p>
              <p>Status: <strong>{data.arrival_dispute_status ?? "open"}</strong> · raised {fmt(data.arrival_disputed_at)}</p>
              {data.arrival_dispute_resolution && (
                <p className="mt-2 text-muted-foreground">{data.arrival_dispute_resolution}</p>
              )}
            </section>
          )}

          {data.no_show && (
            <section className="mt-6 border border-destructive p-4 text-sm text-destructive">
              No-show was reported for this booking.
            </section>
          )}

          <footer className="mt-8 pt-4 border-t border-border text-xs text-muted-foreground">
            Late-arrival deductions are calculated pro-rata against the bartender's hourly rate for minutes missed after the scheduled start, per the contractor agreement. Disputes may be raised within 48 hours of an arrival being logged.
          </footer>
        </div>
      </div>
    </div>
  );
}
